import type { ComputerLesson, QuizQuestion } from '../../lib/types';

interface ComputerTopicPlan {
  slug: string;
  title: string;
  concept: string;
  context: string;
  steps: string[];
  quizPrompt: string;
  quizOptions: string[];
  quizCorrectIndex: number;
  safetyTip: string;
  illustration?: string;
}

interface ComputerWeekPlan {
  week: number;
  theme: string;
  topics: ComputerTopicPlan[];
}

const buildQuiz = (topic: ComputerTopicPlan): QuizQuestion[] => [
  {
    question: topic.quizPrompt,
    options: topic.quizOptions,
    correctIndex: topic.quizCorrectIndex,
  },
  {
    question: 'بهترین شروع برای تمرین این درس کدام است؟',
    options: [topic.steps[0], topic.steps[1], topic.steps[2], 'حذف مرحله ها و انجام تصادفی کارها'],
    correctIndex: 0,
  },
  {
    question: 'کدام گزینه رفتار امن تری در این درس است؟',
    options: [
      topic.safetyTip,
      'اشتراک گذاری رمز عبور با دیگران',
      'بستن برنامه بدون ذخیره',
      'نادیده گرفتن پیام هشدار',
    ],
    correctIndex: 0,
  },
];

const buildComputerLesson = (
  week: number,
  day: number,
  theme: string,
  topic: ComputerTopicPlan,
): ComputerLesson => {
  const weekId = String(week).padStart(2, '0');
  const dayId = String(day).padStart(2, '0');

  return {
    id: `computer-w${weekId}-d${dayId}-${topic.slug}`,
    title: `هفته ${week} - ${topic.title}`,
    explanation:
      `محور هفته: ${theme}\n\n${topic.concept}\n${topic.context}\n\nنکته کاربردی: ${topic.safetyTip}`,
    steps: topic.steps,
    quiz: buildQuiz(topic),
    ...(topic.illustration ? { illustration: topic.illustration } : {}),
  };
};

const computerWeekPlans: ComputerWeekPlan[] = [
  {
    week: 1,
    theme: 'شروع کار با دستگاه و شناخت قطعات',
    topics: [
      {
        slug: 'hardware-parts',
        title: 'شناخت قطعات اصلی کامپیوتر',
        concept: 'در این درس با مانیتور، کیس، صفحه کلید و ماوس آشنا می شوید و نقش هر کدام را می شناسید.',
        context: 'وقتی بدانید هر قطعه چه وظیفه ای دارد، در استفاده روزمره و عیب یابی کمتر سردرگم می شوید.',
        steps: ['به هر قطعه روی میز نگاه کنید و نام آن را تکرار کنید', 'کابل هر قطعه را پیدا کنید', 'یک بار نام قطعات را برای خودتان توضیح دهید'],
        quizPrompt: 'کدام گزینه ابزار اصلی وارد کردن متن است؟',
        quizOptions: ['مانیتور', 'صفحه کلید', 'بلندگو', 'وب کم'],
        quizCorrectIndex: 1,
        safetyTip: 'قبل از جدا کردن کابل ها، سیستم را خاموش کنید.',
      },
      {
        slug: 'power-safe',
        title: 'روشن و خاموش کردن ایمن',
        concept: 'روشن و خاموش کردن درست از آسیب دیدن فایل ها و سیستم جلوگیری می کند.',
        context: 'خاموش کردن ناگهانی ممکن است باعث از دست رفتن اطلاعات یا کند شدن سیستم شود.',
        steps: ['فایل های باز را ذخیره کنید', 'از منوی Start گزینه Shut down را بزنید', 'صبر کنید تا سیستم کامل خاموش شود'],
        quizPrompt: 'قبل از خاموش کردن کامپیوتر چه کاری لازم است؟',
        quizOptions: ['کشیدن کابل برق', 'بستن و ذخیره فایل ها', 'باز گذاشتن برنامه ها', 'فشار دادن طولانی پاور'],
        quizCorrectIndex: 1,
        safetyTip: 'از جدا کردن برق بدون خاموش کردن سیستم خودداری کنید.',
      },
      {
        slug: 'mouse-control',
        title: 'کنترل ماوس و کلیک ها',
        concept: 'در این جلسه کلیک چپ، راست، دابل کلیک و اسکرول را تمرین می کنید.',
        context: 'بیشتر کارهای ویندوز با همین چهار حرکت انجام می شود.',
        steps: ['نشانگر را روی یک آیکون ببرید', 'دابل کلیک کنید تا برنامه باز شود', 'با اسکرول صفحه را بالا و پایین ببرید'],
        quizPrompt: 'برای باز کردن سریع یک آیکون روی دسکتاپ معمولا چه می کنیم؟',
        quizOptions: ['یک بار کلیک راست', 'دابل کلیک چپ', 'کشیدن آیکون به پایین', 'فشار دادن Esc'],
        quizCorrectIndex: 1,
        safetyTip: 'کلیک راست را برای دیدن گزینه ها استفاده کنید، نه حذف عجولانه فایل.',
      },
      {
        slug: 'keyboard-basics',
        title: 'کلیدهای مهم صفحه کلید',
        concept: 'با کلیدهای Enter، Space، Backspace، Shift و Caps Lock آشنا می شوید.',
        context: 'شناخت این کلیدها سرعت تایپ و دقت کار روزمره را بالا می برد.',
        steps: ['یک جمله کوتاه تایپ کنید', 'یک اشتباه را با Backspace اصلاح کنید', 'با Shift حرف بزرگ بنویسید'],
        quizPrompt: 'کدام کلید برای حذف حرف قبلی استفاده می شود؟',
        quizOptions: ['Enter', 'Backspace', 'Shift', 'Tab'],
        quizCorrectIndex: 1,
        safetyTip: 'هنگام تایپ طولانی هر چند دقیقه دست ها را استراحت دهید.',
      },
      {
        slug: 'screen-sound',
        title: 'تنظیم نور صفحه و صدا',
        concept: 'تنظیم درست روشنایی و حجم صدا باعث راحتی چشم و تمرکز بیشتر می شود.',
        context: 'نور خیلی زیاد یا خیلی کم می تواند باعث خستگی چشم شود.',
        steps: ['از تنظیمات سیستم بخش Display را باز کنید', 'روشنایی را متناسب با نور اتاق تنظیم کنید', 'صدا را در حد شنیدن واضح نگه دارید'],
        quizPrompt: 'کدام کار به کاهش خستگی چشم کمک می کند؟',
        quizOptions: ['افزایش حداکثری نور همیشه', 'تنظیم روشنایی متناسب با محیط', 'خاموش کردن کامل صفحه', 'کار در تاریکی کامل'],
        quizCorrectIndex: 1,
        safetyTip: 'در نور کم، روشنایی متوسط انتخاب کنید و فاصله مناسب با مانیتور داشته باشید.',
      },
      {
        slug: 'usb-devices',
        title: 'اتصال و جداسازی فلش و دستگاه جانبی',
        concept: 'اتصال USB ساده است اما جدا کردن اشتباه ممکن است به فایل ها آسیب بزند.',
        context: 'با روش Safely Remove می توانید از خراب شدن اطلاعات جلوگیری کنید.',
        steps: ['فلش را به پورت مناسب وصل کنید', 'فایل مورد نظر را کپی کنید', 'قبل از جدا کردن، گزینه Safely Remove را بزنید'],
        quizPrompt: 'چرا باید از Safely Remove استفاده کنیم؟',
        quizOptions: ['برای سریع تر شدن اینترنت', 'برای محافظت از فایل ها هنگام جدا کردن فلش', 'برای روشن کردن مانیتور', 'برای تغییر زبان سیستم'],
        quizCorrectIndex: 1,
        safetyTip: 'تا وقتی انتقال فایل تمام نشده فلش را جدا نکنید.',
      },
      {
        slug: 'ergonomics',
        title: 'نشستن درست و سلامت کار با کامپیوتر',
        concept: 'وضعیت صحیح بدن هنگام کار باعث کاهش درد گردن، کمر و مچ می شود.',
        context: 'کار طولانی با فرم بدنی نادرست در درازمدت آسیب زا است.',
        steps: ['صندلی را طوری تنظیم کنید که کف پا روی زمین باشد', 'مانیتور را هم سطح چشم قرار دهید', 'هر ۳۰ دقیقه یک استراحت کوتاه انجام دهید'],
        quizPrompt: 'بهترین موقعیت مانیتور کدام است؟',
        quizOptions: ['خیلی پایین تر از چشم', 'هم سطح چشم', 'کنار بدن', 'پشت سر'],
        quizCorrectIndex: 1,
        safetyTip: 'استراحت های کوتاه منظم را بخشی از برنامه کاری قرار دهید.',
      },
    ],
  },
  {
    week: 2,
    theme: 'کار با سیستم عامل و مدیریت پنجره ها',
    topics: [
      {
        slug: 'desktop-taskbar',
        title: 'دسکتاپ و نوار وظیفه',
        concept: 'دسکتاپ میز کار دیجیتال شماست و نوار وظیفه برنامه های باز را نشان می دهد.',
        context: 'با شناخت این دو بخش، جابجایی بین برنامه ها سریع و ساده می شود.',
        steps: ['آیکون های مهم روی دسکتاپ را شناسایی کنید', 'دو برنامه باز کنید', 'از نوار وظیفه بین برنامه ها جابجا شوید'],
        quizPrompt: 'نوار وظیفه بیشتر چه کاربردی دارد؟',
        quizOptions: ['تغییر رنگ صفحه', 'نمایش و جابجایی بین برنامه های باز', 'حذف کامل فایل ها', 'فرمت کردن فلش'],
        quizCorrectIndex: 1,
        safetyTip: 'برنامه های غیرضروری را ببندید تا صفحه شلوغ نشود.',
      },
      {
        slug: 'start-search',
        title: 'منوی Start و جستجوی برنامه ها',
        concept: 'با منوی Start می توانید برنامه ها، تنظیمات و فایل ها را سریع پیدا کنید.',
        context: 'جستجو وقت شما را نسبت به گشتن دستی در پوشه ها کم می کند.',
        steps: ['کلید Start را بزنید', 'نام یک برنامه را جستجو کنید', 'برنامه را Pin کنید تا همیشه در دسترس باشد'],
        quizPrompt: 'برای پیدا کردن سریع یک برنامه چه روشی مناسب تر است؟',
        quizOptions: ['خاموش کردن سیستم', 'استفاده از جستجوی Start', 'حذف همه پوشه ها', 'باز کردن مرورگر'],
        quizCorrectIndex: 1,
        safetyTip: 'برنامه های ناشناس را از نتایج جستجو اجرا نکنید.',
      },
      {
        slug: 'window-management',
        title: 'کوچک کردن، بزرگ کردن و بستن پنجره',
        concept: 'مدیریت پنجره ها به شما کمک می کند چند کار را همزمان منظم انجام دهید.',
        context: 'وقتی پنجره ها کنترل شوند، تمرکز روی کار اصلی ساده تر می شود.',
        steps: ['یک پنجره را Maximize کنید', 'آن را Minimize کنید', 'دوباره باز کرده و در پایان Close کنید'],
        quizPrompt: 'دکمه X در گوشه پنجره چه کاری انجام می دهد؟',
        quizOptions: ['تمام صفحه می کند', 'می بندد', 'ذخیره خودکار می کند', 'کپی می گیرد'],
        quizCorrectIndex: 1,
        safetyTip: 'قبل از بستن پنجره مطمئن شوید فایل ذخیره شده است.',
      },
      {
        slug: 'system-settings',
        title: 'آشنایی با تنظیمات اصلی سیستم',
        concept: 'بخش Settings برای کنترل زبان، شبکه، نمایش و حساب کاربری است.',
        context: 'شناخت تنظیمات پایه باعث استقلال بیشتر در استفاده روزمره می شود.',
        steps: ['Settings را از Start باز کنید', 'بخش Network را مرور کنید', 'یک تنظیم ساده مثل زمان سیستم را بررسی کنید'],
        quizPrompt: 'برای تغییر تنظیمات شبکه به کجا می رویم؟',
        quizOptions: ['Paint', 'Settings', 'Recycle Bin', 'Calculator'],
        quizCorrectIndex: 1,
        safetyTip: 'تنظیماتی را که نمی شناسید بدون راهنما تغییر ندهید.',
      },
      {
        slug: 'language-input',
        title: 'تغییر زبان کیبورد',
        concept: 'برای تایپ فارسی و انگلیسی باید بین زبان های ورودی جابجا شوید.',
        context: 'تسلط بر این مهارت برای فرم ها، ایمیل ها و رمزها ضروری است.',
        steps: ['دو زبان فارسی و انگلیسی را فعال کنید', 'با Alt + Shift بین زبان ها جابجا شوید', 'یک جمله فارسی و یک جمله انگلیسی تایپ کنید'],
        quizPrompt: 'هدف از تغییر زبان ورودی چیست؟',
        quizOptions: ['تغییر اندازه صفحه', 'تایپ با زبان مناسب', 'پاک کردن فایل ها', 'افزایش صدای سیستم'],
        quizCorrectIndex: 1,
        safetyTip: 'قبل از وارد کردن رمز عبور، زبان کیبورد را چک کنید.',
      },
      {
        slug: 'screenshots',
        title: 'گرفتن اسکرین شات',
        concept: 'اسکرین شات برای ثبت خطا، ارسال گزارش و نگهداری اطلاعات بصری کاربردی است.',
        context: 'در آموزش آنلاین و پشتیبانی فنی بسیار از آن استفاده می شود.',
        steps: ['صفحه مورد نظر را باز کنید', 'با کلیدهای مناسب اسکرین شات بگیرید', 'تصویر را در پوشه مشخص ذخیره کنید'],
        quizPrompt: 'اسکرین شات بیشتر برای چه کاری مفید است؟',
        quizOptions: ['افزایش سرعت اینترنت', 'ثبت تصویر صفحه برای گزارش یا آموزش', 'چاپگر را روشن می کند', 'حذف بدافزار'],
        quizCorrectIndex: 1,
        safetyTip: 'در اسکرین شات اطلاعات حساس مثل رمز را نمایش ندهید.',
      },
      {
        slug: 'personalization',
        title: 'شخصی سازی ساده محیط کار',
        concept: 'با تنظیم تصویر پس زمینه، اندازه متن و مرتب سازی آیکون ها محیط راحت تری می سازید.',
        context: 'محیط مرتب باعث کاهش خطا و افزایش سرعت کار می شود.',
        steps: ['پس زمینه مناسب انتخاب کنید', 'اندازه متن را خوانا تنظیم کنید', 'آیکون های پرکاربرد را مرتب بچینید'],
        quizPrompt: 'مزیت اصلی شخصی سازی محیط کار چیست؟',
        quizOptions: ['پاک شدن خودکار فایل ها', 'راحتی و سرعت بیشتر در کار', 'افزایش دمای سیستم', 'تغییر زبان اینترنت'],
        quizCorrectIndex: 1,
        safetyTip: 'فقط تغییرات ظاهری انجام دهید و تنظیمات سیستمی حساس را دست نزنید.',
      },
    ],
  },
  {
    week: 3,
    theme: 'مدیریت فایل و پوشه به شکل حرفه ای',
    topics: [
      {
        slug: 'create-folders',
        title: 'ساخت پوشه های منظم',
        concept: 'پوشه بندی درست، پیدا کردن فایل ها را سریع می کند.',
        context: 'بی نظمی فایل ها باعث اتلاف زمان و اشتباه در ارسال مدارک می شود.',
        steps: ['یک پوشه اصلی برای هفته بسازید', 'زیرپوشه های کاری و شخصی ایجاد کنید', 'فایل ها را در جای درست منتقل کنید'],
        quizPrompt: 'هدف اصلی ساخت پوشه چیست؟',
        quizOptions: ['شلوغ کردن دسکتاپ', 'مرتب سازی و دسترسی سریع به فایل ها', 'خاموش کردن سیستم', 'تغییر رمز'],
        quizCorrectIndex: 1,
        safetyTip: 'نام پوشه ها را واضح و تاریخ دار انتخاب کنید.',
      },
      {
        slug: 'rename-move',
        title: 'تغییر نام و جابجایی فایل',
        concept: 'با نام گذاری دقیق و انتقال صحیح، خطاهای ارسال فایل کمتر می شود.',
        context: 'فایل های مبهم مثل New Document به راحتی گم می شوند.',
        steps: ['یک فایل را Rename کنید', 'نامی شامل موضوع و تاریخ بدهید', 'فایل را به پوشه مرتبط منتقل کنید'],
        quizPrompt: 'نام گذاری دقیق چه فایده ای دارد؟',
        quizOptions: ['زیباتر شدن مانیتور', 'پیدا کردن سریع و جلوگیری از اشتباه', 'افزایش صدا', 'کاهش نور صفحه'],
        quizCorrectIndex: 1,
        safetyTip: 'قبل از جابجایی، مقصد درست را دوباره بررسی کنید.',
      },
      {
        slug: 'copy-cut-paste',
        title: 'کپی، برش و چسباندن',
        concept: 'Copy فایل را تکثیر می کند و Cut فایل را برای جابجایی آماده می کند.',
        context: 'درک تفاوت Copy و Cut از حذف ناخواسته جلوگیری می کند.',
        steps: ['یک فایل را با Copy در پوشه جدید بچسبانید', 'فایل دیگری را با Cut جابجا کنید', 'نتیجه را در مقصد بررسی کنید'],
        quizPrompt: 'کدام گزینه فایل اصلی را در محل قبلی نگه می دارد؟',
        quizOptions: ['Cut', 'Copy', 'Delete', 'Rename'],
        quizCorrectIndex: 1,
        safetyTip: 'برای فایل های مهم ابتدا Copy انجام دهید سپس بررسی کنید.',
      },
      {
        slug: 'downloads',
        title: 'مدیریت پوشه Downloads',
        concept: 'بیشتر فایل های اینترنتی در Downloads ذخیره می شوند و باید مرتب شوند.',
        context: 'انباشت فایل در Downloads باعث سردرگمی و کمبود فضا می شود.',
        steps: ['پوشه Downloads را باز کنید', 'فایل های مهم را به پوشه های موضوعی منتقل کنید', 'فایل های اضافی را حذف کنید'],
        quizPrompt: 'چرا باید پوشه Downloads را مرتب کنیم؟',
        quizOptions: ['برای تغییر زبان سیستم', 'برای دسترسی سریع و جلوگیری از شلوغی', 'برای خاموش شدن خودکار', 'برای حذف کیبورد'],
        quizCorrectIndex: 1,
        safetyTip: 'فایل های ناشناس دانلود شده را قبل از اجرا بررسی کنید.',
      },
      {
        slug: 'recycle-restore',
        title: 'سطل زباله و بازیابی فایل',
        concept: 'Recycle Bin محل موقت فایل های حذف شده است و می توانید فایل را برگردانید.',
        context: 'این قابلیت از حذف اشتباه فایل های مهم جلوگیری می کند.',
        steps: ['فایل آزمایشی را حذف کنید', 'Recycle Bin را باز کنید', 'فایل را Restore کنید'],
        quizPrompt: 'Restore در Recycle Bin چه کاری می کند؟',
        quizOptions: ['فایل را فشرده می کند', 'فایل را به محل قبلی برمی گرداند', 'رمز فایل را عوض می کند', 'فایل را چاپ می کند'],
        quizCorrectIndex: 1,
        safetyTip: 'قبل از Empty کردن Recycle Bin، فایل های قابل بازیابی را بررسی کنید.',
      },
      {
        slug: 'zip-files',
        title: 'فشرده سازی فایل ها',
        concept: 'فایل فشرده (ZIP) برای ارسال سریع تر و دسته بندی چند فایل مفید است.',
        context: 'در ارسال ایمیل یا پیام رسان، فایل فشرده حجم را کاهش می دهد.',
        steps: ['چند فایل را انتخاب کنید', 'گزینه Compress یا Send to ZIP را بزنید', 'فایل zip ساخته شده را بررسی کنید'],
        quizPrompt: 'مزیت فایل ZIP چیست؟',
        quizOptions: ['افزایش اندازه فایل', 'کاهش حجم و یکپارچه سازی چند فایل', 'تغییر زبان متن', 'پاک کردن خودکار پوشه'],
        quizCorrectIndex: 1,
        safetyTip: 'پس از فشرده سازی، فایل اصلی را تا زمان تایید ارسال نگه دارید.',
      },
      {
        slug: 'backup-basics',
        title: 'پشتیبان گیری اولیه',
        concept: 'پشتیبان گیری یعنی داشتن نسخه دوم از فایل های مهم روی فضای دیگر.',
        context: 'در خرابی سیستم یا حذف اشتباه، بکاپ تنها راه سریع بازیابی است.',
        steps: ['فایل های ضروری را انتخاب کنید', 'روی فلش یا فضای ابری کپی کنید', 'تاریخ بکاپ را در نام پوشه ثبت کنید'],
        quizPrompt: 'چرا بکاپ گیری ضروری است؟',
        quizOptions: ['برای تغییر فونت', 'برای جلوگیری از از دست رفتن اطلاعات', 'برای افزایش نور', 'برای نصب بازی'],
        quizCorrectIndex: 1,
        safetyTip: 'برای فایل های مهم برنامه بکاپ هفتگی تنظیم کنید.',
      },
    ],
  },
  {
    week: 4,
    theme: 'اینترنت، ایمیل و ارتباط آنلاین',
    topics: [
      {
        slug: 'browser-basics',
        title: 'مرورگر و نوار آدرس',
        concept: 'مرورگر ابزار ورود به وب است و نوار آدرس محل نوشتن سایت یا جستجو.',
        context: 'شناخت بخش های اصلی مرورگر، استفاده از اینترنت را سریع تر می کند.',
        steps: ['مرورگر را باز کنید', 'یک آدرس معتبر وارد کنید', 'نتیجه را بررسی و صفحه را ببندید'],
        quizPrompt: 'آدرس سایت را در کدام بخش وارد می کنیم؟',
        quizOptions: ['نوار آدرس', 'نوار وظیفه', 'سطل زباله', 'دفترچه یادداشت'],
        quizCorrectIndex: 0,
        safetyTip: 'آدرس سایت های حساس را دقیق بررسی کنید.',
      },
      {
        slug: 'tabs-bookmarks',
        title: 'تب ها، تاریخچه و نشانک',
        concept: 'تب ها برای بازکردن همزمان چند صفحه و نشانک برای ذخیره سایت های مهم است.',
        context: 'این مهارت در مطالعه، خرید و پیگیری کارها بسیار کاربردی است.',
        steps: ['دو تب جدید باز کنید', 'یک سایت را Bookmark کنید', 'تاریخچه مرور را پیدا کنید'],
        quizPrompt: 'Bookmark چه کاربردی دارد؟',
        quizOptions: ['حذف تب ها', 'ذخیره سریع سایت های مهم', 'قطع اینترنت', 'پرینت صفحه'],
        quizCorrectIndex: 1,
        safetyTip: 'اگر سیستم مشترک است، تاریخچه را برای حریم خصوصی مدیریت کنید.',
      },
      {
        slug: 'safe-search',
        title: 'جستجوی امن و ارزیابی نتیجه',
        concept: 'هر نتیجه جستجو معتبر نیست و باید منبع و آدرس آن را بررسی کنید.',
        context: 'جلوگیری از ورود به سایت های جعلی یا اطلاعات نادرست اهمیت بالایی دارد.',
        steps: ['عبارت جستجوی دقیق بنویسید', 'چند نتیجه اول را با دقت بخوانید', 'فقط منبع معتبر را باز کنید'],
        quizPrompt: 'نشانه معتبر بودن یک صفحه چیست؟',
        quizOptions: ['عنوان خیلی هیجانی', 'منبع مشخص و آدرس معتبر', 'درخواست فوری اطلاعات شخصی', 'تبلیغات زیاد'],
        quizCorrectIndex: 1,
        safetyTip: 'به لینک هایی که اطلاعات بانکی می خواهند سریع اعتماد نکنید.',
      },
      {
        slug: 'email-compose',
        title: 'ساخت و ارسال ایمیل',
        concept: 'ایمیل ابزار رسمی ارتباط است و باید موضوع، متن و گیرنده درست داشته باشد.',
        context: 'ارسال ایمیل مرتب و مودبانه تصویر حرفه ای تری از شما می سازد.',
        steps: ['گیرنده و موضوع را وارد کنید', 'متن کوتاه و واضح بنویسید', 'قبل از ارسال یک بار بازخوانی کنید'],
        quizPrompt: 'کدام بخش برای خلاصه پیام مهم است؟',
        quizOptions: ['Subject', 'Drafts', 'Trash', 'Spam'],
        quizCorrectIndex: 0,
        safetyTip: 'قبل از ارسال، آدرس گیرنده را دوباره چک کنید.',
      },
      {
        slug: 'email-attachments',
        title: 'پیوست فایل در ایمیل',
        concept: 'برای ارسال مدارک باید فایل مناسب را Attach کرده و نام فایل را دقیق انتخاب کنید.',
        context: 'پیوست اشتباه یکی از رایج ترین خطاهای کاری است.',
        steps: ['روی Attach کلیک کنید', 'فایل صحیح را انتخاب کنید', 'حجم و نام فایل را قبل ارسال بررسی کنید'],
        quizPrompt: 'برای کاهش اشتباه در پیوست چه کاری مفید است؟',
        quizOptions: ['ارسال بدون بررسی', 'کنترل نام فایل و پیش نمایش پیوست', 'تغییر رمز ایمیل', 'بستن سریع صفحه'],
        quizCorrectIndex: 1,
        safetyTip: 'مدارک حساس را فقط برای گیرنده تاییدشده ارسال کنید.',
      },
      {
        slug: 'video-calls',
        title: 'اصول تماس تصویری',
        concept: 'برای تماس آنلاین باید صدا، دوربین و اینترنت را پیش از شروع بررسی کنید.',
        context: 'آماده سازی قبلی باعث شروع بدون تنش جلسه می شود.',
        steps: ['میکروفون و دوربین را تست کنید', 'لینک جلسه را زودتر باز کنید', 'در محیط آرام به جلسه بپیوندید'],
        quizPrompt: 'بهترین زمان برای تست میکروفون چه زمانی است؟',
        quizOptions: ['بعد از پایان جلسه', 'قبل از شروع جلسه', 'وقتی اینترنت قطع است', 'زمانی که برنامه بسته است'],
        quizCorrectIndex: 1,
        safetyTip: 'در جلسه های عمومی، میکروفون را هنگام عدم صحبت روی بی صدا بگذارید.',
      },
      {
        slug: 'online-etiquette',
        title: 'آداب ارتباط آنلاین',
        concept: 'پیام های کوتاه، محترمانه و دقیق کیفیت ارتباط آنلاین را بالا می برد.',
        context: 'رعایت آداب نوشتاری از سوءتفاهم و تنش جلوگیری می کند.',
        steps: ['سلام و خطاب مناسب بنویسید', 'درخواست خود را واضح بیان کنید', 'پایان مودبانه اضافه کنید'],
        quizPrompt: 'کدام پیام حرفه ای تر است؟',
        quizOptions: ['جواب بده سریع', 'سلام، لطفا فایل را تا عصر ارسال کنید. ممنون.', '؟؟؟؟', 'فرستادی یا نه'],
        quizCorrectIndex: 1,
        safetyTip: 'از ارسال پیام تند یا مبهم در گروه های کاری خودداری کنید.',
      },
    ],
  },
  {
    week: 5,
    theme: 'امنیت دیجیتال و حریم خصوصی',
    topics: [
      {
        slug: 'strong-passwords',
        title: 'ساخت رمز عبور قوی',
        concept: 'رمز قوی ترکیبی از حروف بزرگ و کوچک، عدد و نماد است.',
        context: 'رمز ساده احتمال دسترسی غیرمجاز به حساب را بالا می برد.',
        steps: ['یک عبارت امن بسازید', 'از تکرار رمز قدیمی پرهیز کنید', 'رمز را در جای امن نگهداری کنید'],
        quizPrompt: 'کدام رمز امن تر است؟',
        quizOptions: ['123456', 'password', 'M!na2026Home', 'qwerty'],
        quizCorrectIndex: 2,
        safetyTip: 'برای هر حساب مهم رمز متفاوت داشته باشید.',
      },
      {
        slug: 'two-factor',
        title: 'فعالسازی تایید دو مرحله ای',
        concept: 'Two-Factor Authentication یک لایه امنیتی اضافه روی رمز عبور است.',
        context: 'حتی اگر رمز لو برود، ورود بدون کد دوم سخت می شود.',
        steps: ['وارد تنظیمات امنیت حساب شوید', 'گزینه Two-Factor را فعال کنید', 'کد پشتیبان را ذخیره کنید'],
        quizPrompt: 'مزیت اصلی تایید دو مرحله ای چیست؟',
        quizOptions: ['کاهش حجم فایل', 'افزایش امنیت ورود', 'تغییر رنگ برنامه', 'سریع شدن تایپ'],
        quizCorrectIndex: 1,
        safetyTip: 'کدهای پشتیبان را در جایی امن نگه دارید.',
      },
      {
        slug: 'phishing-alerts',
        title: 'تشخیص پیام فیشینگ',
        concept: 'فیشینگ پیام یا صفحه جعلی برای گرفتن رمز یا اطلاعات بانکی است.',
        context: 'با شناسایی نشانه های فیشینگ می توانید جلوی خسارت مالی را بگیرید.',
        steps: ['فرستنده پیام را بررسی کنید', 'روی لینک های مشکوک کلیک نکنید', 'در صورت شک، از کانال رسمی تایید بگیرید'],
        quizPrompt: 'کدام نشانه احتمال فیشینگ را بالا می برد؟',
        quizOptions: ['دامنه رسمی و درست', 'درخواست فوری رمز یا کد تایید', 'سلام و نام کامل شما', 'فرمت ایمیل سازمانی'],
        quizCorrectIndex: 1,
        safetyTip: 'هیچ سازمان معتبر از شما رمز عبور کامل نمی خواهد.',
      },
      {
        slug: 'safe-updates',
        title: 'به روزرسانی امن سیستم و برنامه ها',
        concept: 'آپدیت ها اغلب مشکلات امنیتی را رفع می کنند و سیستم را پایدارتر می کنند.',
        context: 'تعویق طولانی به روزرسانی، ریسک نفوذ را افزایش می دهد.',
        steps: ['وارد بخش Update شوید', 'آپدیت رسمی را دانلود کنید', 'پس از نصب، سیستم را راه اندازی مجدد کنید'],
        quizPrompt: 'چرا به روزرسانی منظم مهم است؟',
        quizOptions: ['برای پاک کردن کیبورد', 'برای رفع باگ و ضعف امنیتی', 'برای حذف همه فایل ها', 'برای تعویض مانیتور'],
        quizCorrectIndex: 1,
        safetyTip: 'آپدیت را فقط از منبع رسمی سیستم یا سازنده برنامه بگیرید.',
      },
      {
        slug: 'browser-privacy',
        title: 'تنظیمات حریم خصوصی مرورگر',
        concept: 'مرورگر تنظیماتی برای کوکی ها، مجوزها و ردگیری دارد که باید مدیریت شوند.',
        context: 'این تنظیمات می تواند میزان جمع آوری داده های شما را کاهش دهد.',
        steps: ['بخش Privacy مرورگر را باز کنید', 'مجوزهای غیرضروری را غیرفعال کنید', 'کوکی ها و تاریخچه را دوره ای پاک کنید'],
        quizPrompt: 'کدام کار به حفظ حریم خصوصی کمک می کند؟',
        quizOptions: ['فعال کردن همه مجوزها', 'مدیریت مجوزها و پاک سازی دوره ای داده مرور', 'اشتراک گذاری تاریخچه', 'غیرفعال کردن رمز'],
        quizCorrectIndex: 1,
        safetyTip: 'برای سایت های ناشناس مجوز دوربین و میکروفون ندهید.',
      },
      {
        slug: 'antivirus-scan',
        title: 'اسکن امنیتی و آنتی ویروس',
        concept: 'اسکن دوره ای فایل ها و سیستم احتمال آلودگی را کاهش می دهد.',
        context: 'دانلود فایل از منابع ناشناس ریسک بدافزار را بالا می برد.',
        steps: ['آنتی ویروس را به روز کنید', 'اسکن کامل سیستم را اجرا کنید', 'نتایج را بررسی و تهدیدها را قرنطینه کنید'],
        quizPrompt: 'اولین قدم پیش از اسکن کامل چیست؟',
        quizOptions: ['خاموش کردن اینترنت', 'به روز کردن آنتی ویروس', 'حذف همه پوشه ها', 'باز کردن فایل ناشناس'],
        quizCorrectIndex: 1,
        safetyTip: 'پس از اسکن، فایل مشکوک را بدون بررسی باز نکنید.',
      },
      {
        slug: 'app-permissions',
        title: 'مجوزهای برنامه ها',
        concept: 'برنامه ها ممکن است به مخاطبین، دوربین یا موقعیت مکانی دسترسی بخواهند.',
        context: 'کنترل مجوزها از دسترسی غیرضروری به اطلاعات شخصی جلوگیری می کند.',
        steps: ['فهرست برنامه های نصب شده را باز کنید', 'مجوز هر برنامه را بررسی کنید', 'مجوزهای غیرضروری را غیرفعال کنید'],
        quizPrompt: 'چرا بررسی مجوز برنامه مهم است؟',
        quizOptions: ['برای روشن کردن چاپگر', 'برای محافظت از داده های شخصی', 'برای افزایش سرعت تایپ', 'برای تغییر زبان مانیتور'],
        quizCorrectIndex: 1,
        safetyTip: 'فقط در زمان نیاز، مجوزهای حساس را فعال کنید.',
      },
    ],
  },
  {
    week: 6,
    theme: 'بهره وری با نرم افزارهای اداری',
    topics: [
      {
        slug: 'word-basics',
        title: 'ساخت سند در واژه پرداز',
        concept: 'در این درس ایجاد سند جدید، ذخیره و نام گذاری استاندارد را تمرین می کنید.',
        context: 'سند تمیز و منظم برای نامه نگاری و گزارش ضروری است.',
        steps: ['یک سند جدید بسازید', 'یک پاراگراف تایپ کنید', 'سند را با نام مناسب ذخیره کنید'],
        quizPrompt: 'اولین اقدام بعد از باز کردن سند جدید چیست؟',
        quizOptions: ['حذف برنامه', 'نوشتن محتوا و سپس ذخیره', 'ارسال ایمیل فوری', 'تغییر پس زمینه سیستم'],
        quizCorrectIndex: 1,
        safetyTip: 'برای جلوگیری از از دست رفتن کار، ذخیره دوره ای انجام دهید.',
      },
      {
        slug: 'text-formatting',
        title: 'قالب بندی متن',
        concept: 'استفاده از Heading، بولد و فاصله بندی مناسب، خوانایی متن را بالا می برد.',
        context: 'قالب بندی درست، سند شما را حرفه ای و قابل فهم می کند.',
        steps: ['عنوان اصلی را Heading کنید', 'واژه های کلیدی را Bold کنید', 'فاصله خطوط را مناسب تنظیم کنید'],
        quizPrompt: 'هدف اصلی قالب بندی چیست؟',
        quizOptions: ['زیاد کردن حجم فایل', 'افزایش خوانایی و نظم متن', 'خاموش کردن سیستم', 'تغییر رمز وای فای'],
        quizCorrectIndex: 1,
        safetyTip: 'در یک سند از سبک های یکدست استفاده کنید.',
      },
      {
        slug: 'spreadsheet-basics',
        title: 'ورود داده در صفحه گسترده',
        concept: 'سلول، سطر و ستون پایه کار در spreadsheet هستند.',
        context: 'برای مدیریت هزینه، لیست خرید و برنامه کاری بسیار مفید است.',
        steps: ['عنوان ستون ها را وارد کنید', 'چند ردیف داده نمونه بنویسید', 'فایل را ذخیره کنید'],
        quizPrompt: 'واحد اصلی ورود داده در spreadsheet چیست؟',
        quizOptions: ['پوشه', 'سلول', 'صفحه مانیتور', 'تب مرورگر'],
        quizCorrectIndex: 1,
        safetyTip: 'قبل از وارد کردن عدد، فرمت سلول را بررسی کنید.',
      },
      {
        slug: 'spreadsheet-formulas',
        title: 'فرمول های ساده و مرتب سازی',
        concept: 'فرمول های جمع و میانگین محاسبات را سریع و دقیق می کنند.',
        context: 'مرتب سازی داده ها تحلیل اطلاعات را آسان تر می سازد.',
        steps: ['یک ستون عددی ایجاد کنید', 'فرمول SUM را اجرا کنید', 'داده ها را صعودی مرتب کنید'],
        quizPrompt: 'کدام ابزار برای جمع خودکار مفید است؟',
        quizOptions: ['SUM', 'DELETE', 'PASTE', 'PRINT'],
        quizCorrectIndex: 0,
        safetyTip: 'فرمول را پس از کپی در سلول های دیگر دوباره چک کنید.',
      },
      {
        slug: 'slides-basics',
        title: 'ساخت ارائه کوتاه',
        concept: 'در ارائه، اسلاید باید خلاصه، خوانا و تصویری باشد.',
        context: 'ارائه خوب پیام شما را سریع تر منتقل می کند.',
        steps: ['یک قالب ساده انتخاب کنید', 'برای هر اسلاید یک ایده اصلی بنویسید', 'از متن طولانی پرهیز کنید'],
        quizPrompt: 'در اسلاید حرفه ای کدام بهتر است؟',
        quizOptions: ['پاراگراف بسیار بلند', 'نکات کوتاه و واضح', 'رنگ های بسیار تند و شلوغ', 'بدون عنوان'],
        quizCorrectIndex: 1,
        safetyTip: 'فونت خوانا و کنتراست مناسب بین متن و پس زمینه انتخاب کنید.',
      },
      {
        slug: 'calendar-reminders',
        title: 'تقویم و یادآور',
        concept: 'تقویم دیجیتال برای مدیریت قرارها و مهلت ها بسیار کاربردی است.',
        context: 'یادآورهای به موقع باعث کاهش فراموشی کارهای مهم می شود.',
        steps: ['یک رویداد جدید بسازید', 'زمان و مکان را ثبت کنید', 'یادآور قبل از موعد تنظیم کنید'],
        quizPrompt: 'کارکرد اصلی Reminder چیست؟',
        quizOptions: ['پاک کردن فایل', 'یادآوری کار قبل از موعد', 'تغییر نور صفحه', 'افزایش سرعت اینترنت'],
        quizCorrectIndex: 1,
        safetyTip: 'برای کارهای حساس چند یادآور با فاصله زمانی تنظیم کنید.',
      },
      {
        slug: 'cloud-collab',
        title: 'اشتراک گذاری و کار گروهی ابری',
        concept: 'فضای ابری امکان اشتراک گذاری فایل و ویرایش همزمان را فراهم می کند.',
        context: 'در کار تیمی، نسخه واحد فایل از سردرگمی جلوگیری می کند.',
        steps: ['فایل را روی فضای ابری آپلود کنید', 'سطح دسترسی مناسب بدهید', 'تغییرات همزمان اعضا را بررسی کنید'],
        quizPrompt: 'مزیت اصلی همکاری ابری چیست؟',
        quizOptions: ['حذف خودکار فایل', 'ویرایش مشترک روی یک نسخه', 'خاموش کردن اینترنت', 'عدم نیاز به ذخیره'],
        quizCorrectIndex: 1,
        safetyTip: 'دسترسی ویرایش را فقط به افراد ضروری بدهید.',
      },
    ],
  },
  {
    week: 7,
    theme: 'عیب یابی روزمره و حل مشکل',
    topics: [
      {
        slug: 'no-internet',
        title: 'وقتی اینترنت وصل نیست',
        concept: 'بخش زیادی از مشکلات اینترنت با چند بررسی ساده حل می شود.',
        context: 'شروع عیب یابی مرحله ای باعث صرفه جویی در زمان می شود.',
        steps: ['وضعیت وای فای یا کابل را بررسی کنید', 'مودم را یک بار خاموش و روشن کنید', 'با یک سایت معتبر اتصال را تست کنید'],
        quizPrompt: 'اولین قدم منطقی در قطعی اینترنت چیست؟',
        quizOptions: ['تعویض سیستم عامل', 'بررسی اتصال وای فای یا کابل', 'حذف برنامه ها', 'ریست کامل لپ تاپ'],
        quizCorrectIndex: 1,
        safetyTip: 'قبل از تغییرات پیشرفته، بررسی های ساده را کامل انجام دهید.',
      },
      {
        slug: 'slow-system',
        title: 'کند شدن کامپیوتر',
        concept: 'برنامه های زیاد در پس زمینه و فضای کم دیسک از عوامل رایج کندی هستند.',
        context: 'با مدیریت برنامه های startup و پاکسازی فایل های اضافی، سرعت بهتر می شود.',
        steps: ['برنامه های غیرضروری را ببندید', 'فضای دیسک را بررسی و پاکسازی کنید', 'سیستم را راه اندازی مجدد کنید'],
        quizPrompt: 'کدام عامل معمولا باعث کندی می شود؟',
        quizOptions: ['مرتب بودن پوشه ها', 'کمبود فضای ذخیره سازی', 'خاموش بودن بلوتوث', 'استفاده از موس'],
        quizCorrectIndex: 1,
        safetyTip: 'ابزارهای پاکسازی ناشناس را نصب نکنید.',
      },
      {
        slug: 'app-freeze',
        title: 'وقتی برنامه پاسخ نمی دهد',
        concept: 'گاهی برنامه فریز می شود و باید با روش درست بسته و دوباره اجرا شود.',
        context: 'مدیریت صحیح برنامه قفل شده از آسیب به فایل باز جلوگیری می کند.',
        steps: ['چند ثانیه صبر کنید', 'در صورت نیاز برنامه را از Task Manager ببندید', 'برنامه را دوباره باز کنید و فایل را بررسی کنید'],
        quizPrompt: 'در حالت فریز برنامه چه کاری منطقی است؟',
        quizOptions: ['زدن مداوم کلیدها', 'بستن کنترل شده برنامه و اجرای مجدد', 'خاموش کردن برق', 'حذف فایل های سیستم'],
        quizCorrectIndex: 1,
        safetyTip: 'اگر AutoSave فعال است، نسخه بازیابی شده را بررسی کنید.',
      },
      {
        slug: 'printer-fixes',
        title: 'مشکلات رایج چاپگر',
        concept: 'اتصال چاپگر، کاغذ و صف چاپ سه مورد کلیدی در عیب یابی پرینت هستند.',
        context: 'با بررسی مرحله ای معمولا مشکل چاپ بدون کمک فنی حل می شود.',
        steps: ['اتصال کابل یا شبکه چاپگر را چک کنید', 'کاغذ و جوهر را بررسی کنید', 'صف چاپ را پاکسازی و دوباره چاپ کنید'],
        quizPrompt: 'کدام مورد در مشکل چاپ اول بررسی می شود؟',
        quizOptions: ['رنگ پس زمینه دسکتاپ', 'اتصال چاپگر', 'تنظیمات صدا', 'مرورگر اینترنت'],
        quizCorrectIndex: 1,
        safetyTip: 'برای باز کردن درپوش چاپگر ابتدا آن را خاموش کنید.',
      },
      {
        slug: 'storage-full',
        title: 'وقتی حافظه پر می شود',
        concept: 'پر شدن حافظه باعث کندی و خطا در نصب یا ذخیره می شود.',
        context: 'مدیریت فایل های حجیم و انتقال آن ها به فضای دیگر ضروری است.',
        steps: ['فایل های حجیم را شناسایی کنید', 'فایل های غیرضروری را حذف کنید', 'بخشی از داده را به فلش یا ابر منتقل کنید'],
        quizPrompt: 'علت نیاز به آزادسازی فضا چیست؟',
        quizOptions: ['زیباتر شدن دسکتاپ', 'جلوگیری از کندی و خطای ذخیره', 'افزایش نور اتاق', 'فعال شدن دوربین'],
        quizCorrectIndex: 1,
        safetyTip: 'قبل از حذف فایل های مهم نسخه پشتیبان تهیه کنید.',
      },
      {
        slug: 'account-recovery',
        title: 'بازیابی حساب کاربری',
        concept: 'بازیابی امن حساب نیازمند ایمیل/شماره تایید و رعایت مراحل رسمی است.',
        context: 'اقدام عجولانه در حساب های حساس می تواند خطر امنیتی ایجاد کند.',
        steps: ['گزینه Forgot Password را بزنید', 'کد تایید را از مسیر رسمی دریافت کنید', 'رمز جدید قوی بسازید'],
        quizPrompt: 'در بازیابی حساب، کد تایید را از کجا باید دریافت کرد؟',
        quizOptions: ['پیام افراد ناشناس', 'مسیر رسمی سرویس', 'گروه های عمومی', 'هر لینکی در شبکه اجتماعی'],
        quizCorrectIndex: 1,
        safetyTip: 'کد بازیابی را برای هیچ فردی ارسال نکنید.',
      },
      {
        slug: 'support-request',
        title: 'ارسال گزارش مشکل به پشتیبانی',
        concept: 'گزارش خوب شامل شرح دقیق خطا، زمان وقوع و اسکرین شات است.',
        context: 'هرچه گزارش دقیق تر باشد، حل مشکل سریع تر انجام می شود.',
        steps: ['مشکل را کوتاه و دقیق بنویسید', 'زمان و مرحله وقوع خطا را ثبت کنید', 'اسکرین شات مرتبط پیوست کنید'],
        quizPrompt: 'کدام مورد کیفیت گزارش پشتیبانی را بالا می برد؟',
        quizOptions: ['پیام خیلی کلی و مبهم', 'ثبت مرحله دقیق و پیوست تصویر خطا', 'ارسال بدون عنوان', 'تکرار چندباره یک جمله'],
        quizCorrectIndex: 1,
        safetyTip: 'در گزارش، اطلاعات محرمانه را سانسور یا حذف کنید.',
      },
    ],
  },
  {
    week: 8,
    theme: 'پروژه های عملی و جمع بندی نهایی',
    topics: [
      {
        slug: 'project-folder',
        title: 'طراحی ساختار پوشه پروژه',
        concept: 'هر پروژه باید پوشه بندی استاندارد داشته باشد تا فایل ها قابل پیگیری باشند.',
        context: 'این ساختار در کارهای شغلی و آموزشی از آشفتگی جلوگیری می کند.',
        steps: ['پوشه اصلی پروژه را ایجاد کنید', 'زیرپوشه مدارک، تصاویر و خروجی بسازید', 'نام گذاری یکدست برای فایل ها اعمال کنید'],
        quizPrompt: 'هدف اصلی ساختار پوشه پروژه چیست؟',
        quizOptions: ['پیچیده کردن کار', 'نظم و دسترسی سریع به فایل ها', 'افزایش مصرف باتری', 'قطع اینترنت'],
        quizCorrectIndex: 1,
        safetyTip: 'از همان روز اول پروژه، نام گذاری یکدست داشته باشید.',
      },
      {
        slug: 'cv-document',
        title: 'ساخت فایل رزومه ساده',
        concept: 'رزومه پایه باید شامل اطلاعات تماس، مهارت ها و سوابق مرتبط باشد.',
        context: 'رزومه مرتب شانس پاسخ مثبت کارفرما را بیشتر می کند.',
        steps: ['قالب ساده رزومه باز کنید', 'بخش اطلاعات تماس و مهارت ها را تکمیل کنید', 'فایل را با نام استاندارد ذخیره کنید'],
        quizPrompt: 'کدام بخش در رزومه ضروری است؟',
        quizOptions: ['اطلاعات تماس', 'عکس پس زمینه رنگی', 'متن های طولانی نامرتبط', 'فایل های فشرده اضافی'],
        quizCorrectIndex: 0,
        safetyTip: 'قبل از ارسال رزومه، غلط های تایپی را اصلاح کنید.',
      },
      {
        slug: 'budget-sheet',
        title: 'بودجه بندی ماهانه در spreadsheet',
        concept: 'ثبت درآمد و هزینه در جدول، تصمیم مالی دقیق تری می سازد.',
        context: 'وقتی داده ها روشن باشند، مدیریت هزینه های خانوادگی ساده تر می شود.',
        steps: ['ستون درآمد و هزینه تعریف کنید', 'مبالغ ماهانه را وارد کنید', 'مجموع و باقیمانده را با فرمول محاسبه کنید'],
        quizPrompt: 'مزیت اصلی جدول بودجه چیست؟',
        quizOptions: ['افزایش مصرف', 'شفاف شدن وضعیت مالی', 'حذف نیاز به ذخیره', 'تغییر رمز شبکه'],
        quizCorrectIndex: 1,
        safetyTip: 'نسخه پشتیبان فایل مالی را در محل امن نگه دارید.',
      },
      {
        slug: 'online-forms',
        title: 'تکمیل فرم آنلاین',
        concept: 'فرم های آنلاین نیاز به دقت در فیلدها، نوع فایل پیوست و بازبینی نهایی دارند.',
        context: 'اشتباه در فرم های اداری می تواند باعث رد درخواست شود.',
        steps: ['اطلاعات را مطابق مدرک وارد کنید', 'فایل های موردنیاز را پیوست کنید', 'قبل از Submit همه فیلدها را بازبینی کنید'],
        quizPrompt: 'مهم ترین مرحله قبل از Submit چیست؟',
        quizOptions: ['بستن صفحه', 'بازبینی کامل اطلاعات', 'تغییر پسورد ایمیل', 'پرینت بدون بررسی'],
        quizCorrectIndex: 1,
        safetyTip: 'برای فرم رسمی از ایمیل معتبر و شماره تماس در دسترس استفاده کنید.',
      },
      {
        slug: 'scan-and-send',
        title: 'اسکن و ارسال مدارک',
        concept: 'اسکن خوانا با نام گذاری درست، فرآیند ارسال مدارک را استاندارد می کند.',
        context: 'مدرک تار یا ناقص معمولا نیاز به ارسال مجدد دارد.',
        steps: ['مدرک را با نور مناسب اسکن کنید', 'نام فایل را واضح انتخاب کنید', 'مدرک را از طریق ایمیل یا سامانه ارسال کنید'],
        quizPrompt: 'برای ارسال مدرک حرفه ای چه کاری مهم است؟',
        quizOptions: ['نام فایل مبهم', 'اسکن خوانا و نام گذاری واضح', 'حذف بخشی از اطلاعات', 'ارسال چند نسخه تکراری'],
        quizCorrectIndex: 1,
        safetyTip: 'قبل از ارسال، از کامل بودن تصویر و خوانایی متن مطمئن شوید.',
      },
      {
        slug: 'daily-routine',
        title: 'روتین روزانه دیجیتال',
        concept: 'با یک چک لیست ساده می توانید کارهای دیجیتال روزانه را بدون استرس انجام دهید.',
        context: 'ثبات در روتین، بهره وری شما را بالا می برد.',
        steps: ['کارهای ضروری امروز را لیست کنید', 'برای هر کار زمان مشخص بگذارید', 'در پایان روز وضعیت انجام را ثبت کنید'],
        quizPrompt: 'فایده چک لیست روزانه چیست؟',
        quizOptions: ['افزایش فراموشی', 'نظم و پیگیری بهتر کارها', 'کاهش امنیت حساب', 'قطع برنامه ها'],
        quizCorrectIndex: 1,
        safetyTip: 'کارهای حساس مثل پرداخت یا ارسال مدارک را در زمان تمرکز انجام دهید.',
      },
      {
        slug: 'final-checklist',
        title: 'چک لیست نهایی مهارت های کامپیوتری',
        concept: 'این جلسه جمع بندی ۸ هفته تمرین است و نقاط قوت و نیاز به تمرین بیشتر را مشخص می کند.',
        context: 'ارزیابی نهایی مسیر یادگیری بعدی شما را روشن می کند.',
        steps: ['فهرست مهارت های آموخته شده را مرور کنید', 'سه مهارت قوی و سه مهارت نیازمند تمرین را بنویسید', 'برای ماه بعد برنامه تمرین تعیین کنید'],
        quizPrompt: 'چرا مرور نهایی اهمیت دارد؟',
        quizOptions: ['برای شروع از صفر', 'برای شناخت پیشرفت و برنامه ریزی ادامه مسیر', 'برای حذف همه داده ها', 'برای قطع دسترسی اینترنت'],
        quizCorrectIndex: 1,
        safetyTip: 'پس از پایان دوره هم تمرین هفتگی کوتاه را ادامه دهید.',
      },
    ],
  },
];

export const computerLessons: ComputerLesson[] = computerWeekPlans.flatMap((weekPlan) =>
  weekPlan.topics.map((topic, index) =>
    buildComputerLesson(weekPlan.week, index + 1, weekPlan.theme, topic),
  ),
);
